import { LogoMark } from "../../components/Logo";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Car, UtensilsCrossed, ShieldCheck, Clock, IndianRupee, MessageCircle, Smartphone, Star, Share2, MapPin, Phone, Bell } from "lucide-react";
import { Button } from "../../components/ui/button";
import { IMAGES } from "../../lib/constants";
import { publicApi } from "../../lib/api";
import { FeatureGrid, SectionHeading, Steps, StoryCard, CtaBand } from "../../components/marketing/Blocks";

const SAFETY = [
  { icon: ShieldCheck, title: "Verify every ride", text: "As soon as a driver accepts, you see their photo, a photo of the actual vehicle, the number plate and the make & model — so you only get in the right car." },
  { icon: Share2, title: "Share your live trip", text: "Send a one-tap tracking link to family or friends. They follow both your driver and your live location on the map, no app needed." },
  { icon: Phone, title: "SOS & 24/7 support", text: "An in-app SOS button and a support line are a tap away during any ride, with your trip details ready to share with our safety team." },
  { icon: Star, title: "Verified partners only", text: "Every driver and restaurant clears document verification before going live, and riders can report or block anyone who makes them feel unsafe." },
];

const UPDATES = [
  { icon: IndianRupee, title: "Transparent traffic pricing", text: "When live Google Maps traffic is heavy on your assigned route, your fare adjusts to 1.3× once at assignment — and we tell you immediately, so there's never a surprise at the end." },
  { icon: Bell, title: "Timely WhatsApp alerts", text: "Driver assigned, restaurant accepted and driver-on-the-way updates arrive as quick WhatsApp texts. Invoices, refunds, payment confirmations and cancellation charges come by both email and WhatsApp." },
  { icon: MapPin, title: "Richer live sharing", text: "Shared trip links now show the driver's photo and vehicle photo alongside the live map, so whoever is tracking you can see exactly who you're travelling with." },
];

const FEATURES = [
  { icon: Car, title: "Rides you can rely on", text: "Scooters, autorickshaws, taxi cars and jumbo taxis driven by verified partners, across the city and beyond. Upfront fares, no surprises." },
  { icon: UtensilsCrossed, title: "Food from local kitchens", text: "Smoked pork, axone, biryani or a late-night momo run. Order from restaurants across town and track your delivery live." },
  { icon: ShieldCheck, title: "Verified partners", text: "Every driver and restaurant passes document verification before going live. Your safety is not optional." },
  { icon: Clock, title: "Schedule ahead", text: "Airport run at 5am? Book it tonight. Scheduled rides mean a driver is at your door when you need one." },
  { icon: IndianRupee, title: "Fair, transparent pricing", text: "Distance-based fares shown before you book. Pickups within 2 km are free; a far-pickup fee of just ₹30 (2–5 km) or ₹50 (5–8 km) applies only with your approval. Pay by cash, UPI or card." },
  { icon: MessageCircle, title: "In-app chat", text: "Message your driver or the restaurant directly. No phone numbers exchanged unless you want to." },
];

const STEPS = [
  { title: "Tell us where", text: "Pick your pickup and drop, or choose a restaurant and build your order." },
  { title: "See the fare upfront", text: "Compare Scooter, Autorickshaw, Taxi Car and Jumbo Taxi options with live ETAs, then confirm in one tap." },
  { title: "Track it live", text: "Follow your driver on the map, chat if plans change, and rate when you arrive." },
];

const STATS = [
  { value: "4", label: "vehicle classes" },
  { value: "24/7", label: "booking" },
  { value: "100%", label: "verified partners" },
  { value: "Pan-India", label: "and growing" },
];

export default function Landing() {
  const [stories, setStories] = useState([]);
  useEffect(() => {
    publicApi.stories().then(setStories).catch(() => setStories([]));
  }, []);

  return (
    <>
      {/* Hero */}
      <section className="relative overflow-hidden kt-grain">
        <div className="absolute inset-x-0 top-0 -z-10 h-[520px] bg-gradient-to-b from-brand-50 to-surface" />
        <div className="kt-container grid items-center gap-12 pb-16 pt-14 lg:grid-cols-[1.05fr_1fr] lg:pb-28 lg:pt-20">
          <div className="kt-fade-up">
            <p className="kt-eyebrow inline-flex items-center gap-2"><LogoMark className="h-5 w-5" /> Seamless rides. Fresh deliveries.</p>
            <h1 className="kt-h1 mt-5">
              Your ride and your dinner,<br className="hidden sm:block" /> <span className="text-brand">one trusted app.</span>
            </h1>
            <p className="kt-lead mt-6 max-w-xl">
              KohimaTaxi &amp; Delivery connects you with verified drivers and the kitchens you love. Book a ride, order food, and track everything live — wherever you are in India.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button asChild size="lg" className="h-12 rounded-full bg-brand px-7 text-base text-white hover:bg-brand-600" data-testid="hero-book-ride">
                <Link to="/auth/signup">Book a ride <ArrowRight className="ml-2 h-4 w-4" /></Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="h-12 rounded-full border-ink/15 px-7 text-base text-ink hover:bg-surface-200" data-testid="hero-order-food">
                <Link to="/auth/signup">Order food</Link>
              </Button>
            </div>
            <p className="mt-6 inline-flex items-center gap-2 rounded-full border border-brand/20 bg-brand-50 px-4 py-1.5 text-xs font-semibold text-brand-700" data-testid="made-in-naga">
              Designed, Built &amp; Made in Naga Hills (Nagaland &middot; India)
            </p>
            <dl className="mt-10 grid grid-cols-2 gap-6 sm:grid-cols-4">
              {STATS.map((s) => (
                <div key={s.label}>
                  <dt className="text-2xl font-bold text-ink">{s.value}</dt>
                  <dd className="text-xs uppercase tracking-wider text-ink-muted">{s.label}</dd>
                </div>
              ))}
            </dl>
          </div>
          <div className="kt-fade-up kt-delay-2 relative">
            <div className="absolute -inset-6 -z-10 rounded-[2.5rem] bg-graphite-100/50 blur-3xl" />
            <img src={IMAGES.heroTaxiCar} alt="White taxi car on a scenic Nagaland hill road" className="aspect-[4/3] w-full rounded-[1.75rem] object-cover shadow-2xl shadow-ink/25" />
            <img src={IMAGES.smallTaxi} alt="Small city taxi" className="absolute -top-5 -left-5 hidden h-28 w-28 rounded-2xl border-4 border-surface object-cover shadow-xl sm:block" loading="lazy" />
            <img src={IMAGES.nagaDriver} alt="Naga taxi driver beside his car" className="absolute -right-5 -bottom-8 hidden h-32 w-28 rounded-2xl border-4 border-surface object-cover shadow-xl md:block" loading="lazy" />
            <div className="kt-card absolute -bottom-6 -left-4 flex items-center gap-3 p-4 sm:-left-8">
              <span className="relative flex h-10 w-10 items-center justify-center rounded-full bg-graphite text-white kt-pulse-ring"><Car className="h-5 w-5" /></span>
              <div>
                <p className="text-sm font-semibold text-ink">Car &amp; scooter on demand</p>
                <p className="text-xs text-ink-muted">Taxi Car &middot; 4 min away</p>
              </div>
            </div>
            <div className="kt-card absolute -right-3 top-6 flex items-center gap-2 px-3 py-2 sm:-right-6">
              <Star className="h-4 w-4 fill-brand text-brand" />
              <span className="text-sm font-semibold text-ink">4.9</span>
              <span className="text-xs text-ink-muted">customer rating</span>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="kt-container py-16 lg:py-24">
        <SectionHeading eyebrow="Why KohimaTaxi" title="Built for busy streets, late nights and hungry evenings." lead="Everything a city super-app does, tuned for the way India actually moves." />
        <div className="mt-12"><FeatureGrid items={FEATURES} /></div>
      </section>

      {/* How it works */}
      <section className="bg-surface-200/60 py-16 lg:py-24">
        <div className="kt-container grid gap-12 lg:grid-cols-[1fr_1.1fr] lg:items-center">
          <div>
            <SectionHeading eyebrow="How it works" title="Three taps from door to destination." />
            <div className="mt-10"><Steps steps={STEPS} /></div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <img src={IMAGES.taxiStreet} alt="Taxi on a city street" className="aspect-[3/4] w-full rounded-3xl object-cover" loading="lazy" />
            <img src={IMAGES.foodDeliveryRider} alt="Food delivery rider on a scooter" className="mt-8 aspect-[3/4] w-full rounded-3xl object-cover" loading="lazy" />
          </div>
        </div>
      </section>

      {/* Safety */}
      <section className="kt-container py-16 lg:py-24" data-testid="landing-safety">
        <SectionHeading eyebrow="Your safety" title="Know who's picking you up — every single time." lead="Safety features built into every ride, so you and the people who care about you can relax." />
        <div className="mt-12"><FeatureGrid items={SAFETY} /></div>
      </section>

      {/* What's new */}
      <section className="bg-surface-200/60 py-16 lg:py-24" data-testid="landing-updates">
        <div className="kt-container">
          <SectionHeading eyebrow="What's new" title="Fresh updates for riders and drivers." lead="We keep improving fares, safety and communication. Here's what changed recently." />
          <div className="mt-12"><FeatureGrid items={UPDATES} /></div>
        </div>
      </section>

      {/* Stories */}
      <section className="kt-container py-16 lg:py-24">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <SectionHeading eyebrow="Community stories" title="People who keep cities moving." />
          <Button asChild variant="ghost" className="rounded-full text-brand hover:bg-brand-50"><Link to="/stories">All stories <ArrowRight className="ml-1 h-4 w-4" /></Link></Button>
        </div>
        <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-3" data-testid="landing-stories">
          {stories.slice(0, 3).map((s) => <StoryCard key={s.story_id} story={s} />)}
          {stories.length === 0 && [0, 1, 2].map((i) => <div key={i} className="kt-card h-80 animate-pulse bg-surface-200" />)}
        </div>
      </section>

      {/* Role CTAs */}
      <section className="kt-container pb-8">
        <div className="grid gap-6 md:grid-cols-3">
          {[
            { to: "/for-customers", title: "For customers", text: "Book rides and order food. Pay how you like.", img: IMAGES.taxiCity, icon: Smartphone },
            { to: "/for-drivers", title: "For drivers", text: "Set your own hours. Earn on rides and deliveries.", img: IMAGES.happyDriver, icon: Car },
            { to: "/for-restaurants", title: "For restaurants", text: "Reach new customers and manage orders in one place.", img: IMAGES.chefs, icon: UtensilsCrossed },
          ].map((c) => (
            <Link key={c.to} to={c.to} className="group relative overflow-hidden rounded-3xl text-surface" data-testid={`role-card-${c.title.replace(/\s+/g, "-").toLowerCase()}`}>
              <img src={c.img} alt={c.title} className="aspect-[4/5] w-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />
              <div className="absolute inset-0 bg-gradient-to-t from-ink/90 via-ink/30 to-transparent" />
              <div className="absolute inset-x-0 bottom-0 p-6">
                <c.icon className="h-6 w-6 text-brand-200" />
                <h3 className="mt-3 text-2xl font-bold">{c.title}</h3>
                <p className="mt-1 text-sm text-surface/80">{c.text}</p>
                <span className="mt-4 inline-flex items-center text-sm font-semibold text-brand-200">Learn more <ArrowRight className="ml-1 h-4 w-4 transition-transform group-hover:translate-x-1" /></span>
              </div>
            </Link>
          ))}
        </div>
      </section>

      <CtaBand title="Ready to move with us?" text="Create a free account in under a minute. Customers, drivers and restaurants all start here." cta={{ to: "/auth/signup", label: "Create account" }} secondary={{ to: "/auth/login", label: "I already have one" }} />
    </>
  );
}
